import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '../hooks/useMediaQuery';
import useHistoryBack from '../hooks/useHistoryBack';
import usePresence from '../hooks/usePresence';
import useModalLayer from '../hooks/useModalLayer';
import useDragDismiss from '../hooks/useDragDismiss';

// Drawers can stack (a confirmation over the item form); only the first one to
// open pins the page, and only the last one to close lets it go.
let pageLocks = 0;

const ModalDrawer = ({ isOpen, onClose, title, children, footer, theme, headerActions, beforeBody, drawerId = 'modalDrawer' }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const titleId = useId();
    const wrapRef = useRef(null);
    const panelRef = useRef(null);

    // Handle system back button on mobile. drawerId keys the shared history-back
    // registry: callers that can appear more than once on a page (e.g. F25
    // explanations) must pass a unique id so they don't collide.
    useHistoryBack(isOpen && isMobile, onClose, true, drawerId);

    // 400ms: the panel's .38s slide plus the wrapper's .4s visibility delay
    const [isMounted, isAnimating] = usePresence(isOpen, 400);
    useModalLayer(isOpen && isMounted, onClose, wrapRef, panelRef);
    // full-screen on a phone, it leaves the way it came: swipe the header right
    const onHeadPointerDown = useDragDismiss(panelRef, onClose, { axis: 'x', enabled: isMobile });

    useEffect(() => {
        if (!isMounted) return undefined;
        if (pageLocks++ > 0) return () => { pageLocks--; };
        // Pin the page where it is: fixed, shifted up by the scroll offset.
        const scrollY = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.documentElement.style.overflow = 'hidden';
        document.body.classList.add('modal-drawer-open');
        // Prevent touch scrolling on mobile
        document.body.style.touchAction = 'none';

        // The restore lives in the cleanup: it is the only code that runs on
        // close, and it runs before anything else has cleared `top`. (It used to
        // sit in an else-branch that read `top` after this had already wiped
        // it, so every close left the page at the top.)
        return () => {
            if (--pageLocks > 0) return;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.documentElement.style.overflow = '';
            document.body.style.touchAction = '';
            document.body.classList.remove('modal-drawer-open');
            // 'instant': the global smooth scrolling would animate this from the top
            window.scrollTo({ top: scrollY, behavior: 'instant' });
        };
    }, [isMounted]);

    if (typeof document === 'undefined' || !isMounted) {
        return null;
    }

    return createPortal(
        <div ref={wrapRef} className={`drawer-wrap ${isAnimating ? 'open' : ''}`}>
            {/* Backdrop - rendered first so drawer appears on top */}
            <div 
                className="drawer-backdrop"
                onClick={onClose} 
                aria-hidden="true" 
            />
            
            {/* Drawer Panel - rendered after backdrop for proper z-index stacking */}
            <div
                ref={panelRef}
                className="drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="drawer-head" onPointerDown={onHeadPointerDown}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, justifyContent: 'space-between' }}>
                        {typeof title === 'string' ? (
                            <h3 id={titleId} className="drawer-title">{title}</h3>
                        ) : (
                            <div id={titleId} className="drawer-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>{title}</div>
                        )}
                        {headerActions && (
                            <div className="drawer-head-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {headerActions}
                            </div>
                        )}
                    </div>
                    <button 
                        onClick={onClose} 
                        className="icon-btn" 
                        aria-label={t('general.close')}
                    >
                        <X size={20} />
                    </button>
                </div>
                
                {/* A fixed strip between the head and the scrolling body — the
                    event drawer's step rail lives here, so it stays visible
                    while the form under it scrolls. */}
                {beforeBody}

                {/* Body */}
                <div className="drawer-body">
                    {children}
                </div>
                
                {/* Footer */}
                {footer && (
                    <div className="drawer-foot">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default ModalDrawer;
